import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { openErrorNotification } from "../utils/toast";
import axios from "axios";
import ConfirmModal from "../components/ConfirmModal";

const AutoLogout = ({ timeout }) => {
  const [isActive, setIsActive] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const logoutScheduledRef = useRef(false);
  const currentTimeoutRef = useRef(timeout); // Track current timeout value
  const location = useLocation();

  const isLoggedIn = sessionStorage.getItem("userData");
  const isLoginPage = location.pathname === "/";

  const logout = () => {
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("customerInfo");
    sessionStorage.removeItem("engageData");
    openErrorNotification("Your login session has expired.");
    window.location.replace("/");
  };

  const clearAllTimers = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  };

  const resetTimer = () => {
    // Don't reset if logout is already scheduled
    if (logoutScheduledRef.current) {
      return;
    }

    clearAllTimers();
    setShowModal(false);
    setIsActive(true);

    const currentTimeout = currentTimeoutRef.current;

    // Set warning timer for 1 minute before timeout
    const warningTime = currentTimeout - 60000;
    warningTimerRef.current = setTimeout(() => {
      setShowModal(true);
    }, warningTime);

    // Set logout timer
    timerRef.current = setTimeout(() => {
      setIsActive(false);
    }, currentTimeout);
  };

  const handleExtendSession = () => {
    // Reset the logout scheduled flag
    logoutScheduledRef.current = false;

    // Update current timeout by adding 15 minutes (900000 ms)
    currentTimeoutRef.current = timeout + 900000;

    // Close modal first
    setShowModal(false);

    // Clear all timers
    clearAllTimers();

    const extendedTimeout = currentTimeoutRef.current;

    // Set new warning timer
    const warningTime = extendedTimeout - 60000;
    warningTimerRef.current = setTimeout(() => {
      setShowModal(true);
    }, warningTime);

    // Set new logout timer
    timerRef.current = setTimeout(() => {
      setIsActive(false);
    }, extendedTimeout);
  };

  const handleDeclineExtension = () => {
    // Set flag to ignore activity for 3 seconds
    logoutScheduledRef.current = true;

    // Close the modal
    setShowModal(false);

    // After 3 seconds, re-enable activity tracking but DON'T reset the timer
    // The original logout timer is still running
    setTimeout(() => {
      logoutScheduledRef.current = false;
    }, 3000);
  };

  useEffect(() => {
    // Don't run logout logic on login page
    if (isLoginPage) return;

    if (!isActive && isLoggedIn) {
      clearAllTimers();
      logout();
    }
  }, [isActive, isLoggedIn, isLoginPage]);

  useEffect(() => {
    // Don't set up activity listeners on login page
    if (isLoginPage) return;

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    const handleActivity = () => {
      // Check logout scheduled flag FIRST
      if (logoutScheduledRef.current) {
        return;
      }

      // Then check if modal is showing
      if (showModal) {
        return;
      }

      setIsActive(true);
      resetTimer();
    };

    // Only add event listeners if modal is NOT showing AND logout is NOT scheduled
    if (!showModal && !logoutScheduledRef.current) {
      events.forEach((event) => window.addEventListener(event, handleActivity));
    }

    // Only reset timer on initial mount
    if (
      !showModal &&
      !warningTimerRef.current &&
      !timerRef.current &&
      !logoutScheduledRef.current
    ) {
      resetTimer();
    }

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [timeout, isLoginPage, showModal]);

  // Separate cleanup effect for component unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  useEffect(() => {
    // Don't set up axios interceptor on login page
    if (isLoginPage) return;

    // Add Axios response interceptor for handling 403 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if ([401, 403].includes(error.response && error.response.status)) {
          clearAllTimers();
          logout();
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isLoginPage]);

  // Don't render modal on login page
  if (isLoginPage) {
    return null;
  }

  return (
    <ConfirmModal
      showModal={showModal}
      setShowModal={setShowModal}
      onDeleteConfirm={handleExtendSession}
      setSelectedRowKeys={() => {}}
      content="You have been inactive. Would you like to extend your session by 15 minutes?"
      handleIgnore={handleDeclineExtension}
    />
  );
};

export default AutoLogout;
